import { engineComponent, Utils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Reel } from "../GameComponents/Reel";
import { Symbol } from "../GameComponents/Symbol";
import { CustomUtils } from "../Utils/CustomUtils";
import { Symbols } from "../GameComponents/Symbols";
import { Tweens } from "../Components/Effects/Tweens";
import { MachineExtended } from "./MachineExtended";
import { Communication } from "neo-games";
import { PlayList } from "../Components/Sounds/PlayList";

/**
 * ReelExtended handles:
 * - z-order
 * - usage of Normal instead of Rotation
 */
@engineComponent(ComponentTypes.DisplayObject)
export class ReelExtended extends Reel {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static readonly NudgeReelStopped: string = "nudgeReelStopped";

    //******************************************************************************************************************
    //  SETTINGS
    //******************************************************************************************************************

    public static readonly NudgeSpinDuration: number = 60; //in frames

    public static readonly NudgeSpinDelay: number = 0; //in frames

    public static readonly NudgeSpinReelDelay: number = 5; //in frames

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    public static Tease: boolean = false;

    private nudgeCurrentPartIndex: number;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();
    }

    public initialize(
        previousReel: Reel,
        nextReel: Reel,
        index: number,
        symbols: Symbols,
        strip: Array<number>,
        positionX: number,
        positionY: number,
        symbolCount: number,
        symbolTopCount: number,
        symbolBottomCount: number,
        symbolWidth: number,
        symbolHeight: number,
        startDelay: number,
        stopDelay: number,
    ): void {
        super.initialize(
            previousReel,
            nextReel,
            index,
            symbols,
            strip,
            positionX,
            positionY,
            symbolCount,
            symbolTopCount,
            symbolBottomCount,
            symbolWidth,
            symbolHeight,
            startDelay,
            stopDelay,
        );
    }

    //******************************************************************************************************************
    //  SHOW SYMBOLS, SPIN ROLL
    //******************************************************************************************************************

    /**
     *
     * @param {boolean} useTransform Use current symbols from WinAnimator.
     */
    public showSymbols(useTransform: boolean = false): void {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.stoppingCounter = 0;
        this.isTeasing = false;
        this.teasingSymbols = [];
        this.nudgeCurrentPartIndex = 0;

        //correct index based on previous spin (nudge animation)
        this.data.index = this.getCorrectStripIndex(this.data, this.strip);

        this.x = this.positionX;
        this.y = this.positionY;

        if (!useTransform) {
            while (this.currentSymbols.length > 0) {
                this.returnSymbol(this.currentSymbols.pop());
            }
        }

        let bottomSymbols: Array<Symbol> = [];
        let topSymbols: Array<Symbol> = [];

        for (let i = 0; i < this.symbolBottomCount + this.symbolCount + this.symbolTopCount; i++) {
            let symbolId: number;
            let symbolCase: string;

            if (i < this.symbolBottomCount) {
                // Bottom symbols
                symbolId = this.getOuterSymbol(i);
                symbolCase = i === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (i < this.symbolBottomCount + this.symbolCount) {
                // Visible symbols
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - i - 1];
                symbolCase = Symbol.CaseRotation;
            } else {
                //Top symbols
                symbolId = this.getOuterSymbol(i);
                symbolCase = i === this.symbolBottomCount + this.symbolCount ? Symbol.CaseRotation : Symbol.CaseBlur;
            }

            if (!useTransform) {
                // when we don't want use symbols from WinAnimator
                this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));
            } else {
                // currentSymbols are used from WinAnimator. We only need to add new top and bottom symbols
                if (i < this.symbolBottomCount) {
                    bottomSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));
                } else if (i > this.symbolBottomCount + this.symbolCount - 1) {
                    topSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));
                }
            }
        }

        if (useTransform) {
            while (bottomSymbols.length > 0) {
                this.currentSymbols.push(bottomSymbols.shift());
            }

            while (topSymbols.length > 0) {
                this.currentSymbols.unshift(topSymbols.pop());
            }
        }

        this.updateNudgeSymbolsCase();

        for (let i = 0; i < this.currentSymbols.length; i++) {
            this.addChild(this.currentSymbols[i]);

            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        this.sortChildrenByZindex(this);
    }

    /**
     * Prepare reel with correct case of symbols and update current nudge part index.
     * We dont have to update bottom symbol, it's automatically correct.
     * We also dont have to update visible symbols, if there was nudge or something, cases are right.
     * So we just need to check top symbols and prepare correct cases for them.
     */
    private updateNudgeSymbolsCase(): void {
        for (let i = this.currentSymbols.length - 1 - this.symbolBottomCount - this.symbolCount; i >= 0; i--) {
            this.updateNudgeSymbolCase(this.currentSymbols[i].id + 1, this.currentSymbols[i]);
        }
    }

    /**
     * Correct current strip index. If there was nudge animation in previous spin, we need to update current position of strip index
     * @param currentData - current data before nudge animation
     * @param strip - reel strip
     */
    private getCorrectStripIndex(currentData: Communication.Game.IReel, strip: Array<number>): number {
        let index = currentData.index;

        //if there was no nudge, return current index
        if (currentData.transform == null) {
            return index;
        }

        //if there was no nudge, return current index
        if (CustomUtils.NudgeSymbols.indexOf(currentData.transform[0]) == -1) {
            return index;
        }

        // const nudgeSymbolId = currentData.transform[0];
        const direction = this.getNudgeDirection();
        const nudgeCount = this.getNudgeSymbolsCount();

        return (index - nudgeCount * direction + strip.length) % strip.length;
    }

    //******************************************************************************************************************
    //  SPIN ROLL
    //******************************************************************************************************************

    protected updateReel(): void {
        //due to nudge animation, we have to keep final bottom symbol
        const keepBottomSymbols =
            this.stoppingCounter > this.symbolTopCount + this.symbolBottomCount &&
            MachineExtended.NudgeReelIndexes.indexOf(this.index) > -1;

        if (!keepBottomSymbols) {
            this.returnSymbol(this.currentSymbols.pop());
        }

        if (this.shouldBeStopping && !this.isStopping) {
            if (this.previousReel === null) {
                this.isStopping = true;
            } else if (this.previousReel.isStopping) {
                this.isStopping = true;
            }
        }

        this.stripIndex = (this.stripIndex - 1 + this.strip.length) % this.strip.length;

        let symbolId = this.strip[this.stripIndex];
        let symbolCase = Symbol.CaseBlur;

        if (this.isStopping) {
            //update stopping strip
            if (this.stoppingCounter == 0) {
                this.prepareReelForStopping();
            }

            if (this.stoppingCounter < this.symbolBottomCount) {
                // Bottom over reel symbol
                symbolId = this.getOuterSymbol(this.stoppingCounter);
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (this.stoppingCounter < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1];
                symbolCase = Symbol.CaseRotation;
            } else {
                // Upper over reel symbol
                symbolId = this.getOuterSymbol(this.stoppingCounter);
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount + this.symbolCount
                        ? Symbol.CaseRotation
                        : Symbol.CaseBlur;
            }
            this.stoppingCounter++;
        }

        this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase));
        this.addChild(this.currentSymbols[0]);

        //decide which part of nudge symbol will be empty or normal (the upper one is visible, others are empty)
        this.updateNudgeSymbolCase(symbolId, this.currentSymbols[0]);

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        this.sortChildrenByZindex(this);

        if (
            this.isStopping &&
            this.stoppingCounter === this.symbolBottomCount + this.symbolCount + this.symbolTopCount
        ) {
            this.spinPostRoll();
        } else {
            this.spinRoll();
        }
    }

    //******************************************************************************************************************
    //  NUDGE SYMBOL SPIN HELPER
    //******************************************************************************************************************

    /**
     * Handle all possible scenarios with nudge symbol. We have to use 6 top symbols to handle all cases.
     * Requirements
     *      1 - handle case, when we already have one or two parts of nudge
     *          symbol on the reel before stopping
     *      2 - cannot one nudge symbol overlap another one
     *      3 - must be at least 1 small symbol between two nudge symbols
     *      4 - if nudge animation will be proceed after spin complete, take care of outer symbols,
     *          they must be pick from strip by final strip index
     */
    private prepareReelForStopping(): void {
        //handle first requirement
        this.fixUncompletedNudgeSymbol();

        const overlapNudgePartsCount = this.getOverlapNudgePartsCount();

        //handle second requirement
        this.fixOverlapNudgeSymbol(overlapNudgePartsCount);

        //handle third requirement
        this.fixSpaceBetweenNudgeSymbols(overlapNudgePartsCount);

        //handle forth requirement
        this.fixFinalBottomSymbol(overlapNudgePartsCount);

        //after all of this, we reset this value to 0, but the final screen should expect other value
        this.calculateNudgeCurrentPartIndex(overlapNudgePartsCount);
    }

    /**
     * Handle case, when we already have one or two parts of nudge symbol on the reel before stopping
     */
    private fixUncompletedNudgeSymbol(): void {
        //we dont have uncompleted nudge symbol
        if (this.nudgeCurrentPartIndex == 0) {
            return;
        }

        for (let i = 0; i < this.nudgeCurrentPartIndex; i++) {
            //ToDo nechat tento checker ? hovori, ci nahodou nereplacujem iny ako nudge symbol co by znamenalo chybu
            if (CustomUtils.NudgeSymbols.indexOf(this.currentSymbols[i].id + 1) == -1) {
                console.error(
                    `Logic error, replaced symbol is not nudge symbol: (symbolId: ${this.currentSymbols[i].id + 1})`,
                );
            }

            const randomId = Math.floor(Math.random() * 6);
            this.returnSymbol(this.currentSymbols[i]);
            this.currentSymbols[i] = this.symbols.getSymbol(randomId, Symbol.CaseBlur);
            this.addChild(this.currentSymbols[i]);
        }

        //now we have all nudge symbols completed, reset index to 2
        this.nudgeCurrentPartIndex = 0;
    }

    /**
     * Set all overlapped symbols case to EMPTY. Check if overlapped symbol is nudge symbol.
     * If so, replace whole nudge symbol with random symbols and set correct case.
     * @param overlapNudgePartsCount - count of nudge symbol parts we need to process over currentSymbols array
     */
    private fixOverlapNudgeSymbol(overlapNudgePartsCount: number): void {
        //there are no overlap parts
        if (overlapNudgePartsCount < 1) {
            return;
        }

        let anotherNudgeSymbolStartIndex = -1;
        for (let i = overlapNudgePartsCount - 1; i >= 0; i--) {
            const symbol = this.currentSymbols[i];

            if (CustomUtils.NudgeSymbols.indexOf(symbol.id + 1) > -1) {
                anotherNudgeSymbolStartIndex = i;
            }

            //change to to some of the nudge symbol, doesnt matter the id, its empty part
            //due to teasing and putting symbols to nowin except nudge symbol
            this.currentSymbols[i] = this.symbols.getSymbol(CustomUtils.NudgeSymbols[0] - 1, Symbol.CaseEmpty);
            this.currentSymbols[i].x = symbol.x;
            this.currentSymbols[i].y = symbol.y;
            this.symbols.returnSymbol(symbol);
        }

        //replace another overlap nudge symbol with random symbols
        //keep in mind, all replaced overlap symbols must be EMPTY
        if (anotherNudgeSymbolStartIndex > -1) {
            for (
                let i = anotherNudgeSymbolStartIndex;
                i < CustomUtils.NudgeSymbolPartCount + anotherNudgeSymbolStartIndex;
                i++
            ) {
                //if overlapped symbol, we need to have this part as a nudge empty part
                const randomId =
                    i < overlapNudgePartsCount ? CustomUtils.NudgeSymbols[0] - 1 : Math.floor(Math.random() * 6);
                this.returnSymbol(this.currentSymbols[i]);
                this.currentSymbols[i] = this.symbols.getSymbol(
                    randomId,
                    i < overlapNudgePartsCount ? Symbol.CaseEmpty : Symbol.CaseBlur,
                );
                this.addChild(this.currentSymbols[i]);
            }
        }
    }

    /**
     * In case, we have two nudge symbols, check if there is at least one symbol space between them
     * If not, we have to replace second nudge symbol
     * We dont have to check overlap nudge symbol thanks to previous function "fixOverlapNudgeSymbol"
     * @param overlapNudgePartsCount - count of nudge symbol parts we need to process over currentSymbols array
     */
    private fixSpaceBetweenNudgeSymbols(overlapNudgePartsCount: number): void {
        //there is nothing to check
        if (overlapNudgePartsCount == -1) {
            return;
        }

        //there is no nudge symbol right after our final nudge symbol, just return
        if (CustomUtils.NudgeSymbols.indexOf(this.currentSymbols[overlapNudgePartsCount].id + 1) == -1) {
            return;
        }

        //replace next 3 symbols with random symbol
        for (let i = overlapNudgePartsCount; i < CustomUtils.NudgeSymbolPartCount + overlapNudgePartsCount; i++) {
            const randomId = Math.floor(Math.random() * 6);
            this.returnSymbol(this.currentSymbols[i]);
            this.currentSymbols[i] = this.symbols.getSymbol(randomId, Symbol.CaseBlur);
            this.addChild(this.currentSymbols[i]);
        }
    }

    /**
     * If there is nudge animaiton, we have to care about bottom symbol after nudge animation.
     * @param overlapNudgePartsCount - count of nudge symbol parts we need to process over currentSymbols array
     */
    private fixFinalBottomSymbol(overlapNudgePartsCount: number): void {
        //we dont have to fix bottom symbol
        if (overlapNudgePartsCount == -1) {
            return;
        }

        //bottom symbol is nudge, there is no nudge animation
        if (overlapNudgePartsCount == 2) {
            return;
        }

        const finalBottomSymbolId =
            this.strip[
                (this.data.index +
                    this.symbolCount +
                    this.symbolBottomCount +
                    overlapNudgePartsCount +
                    this.strip.length) %
                    this.strip.length
            ];
        this.returnSymbol(this.currentSymbols[overlapNudgePartsCount]);
        this.currentSymbols[overlapNudgePartsCount] = this.symbols.getSymbol(finalBottomSymbolId - 1, Symbol.CaseBlur);
        this.addChild(this.currentSymbols[overlapNudgePartsCount]);
    }

    private calculateNudgeCurrentPartIndex(overlapNudgePartsCount: number): void {
        //we dont have to do anything
        if (overlapNudgePartsCount == -1) {
            return;
        }

        this.nudgeCurrentPartIndex = overlapNudgePartsCount;
    }

    /**
     * Returns count of nudge symbol parts we need to process over currentSymbols array
     * Returns 2 if there are 2 overlap parts
     * Returns 1 if there is only 1 overlap part
     * Returns 0 if there is no overlap part, but the last part is bottom symbol
     * Returns -1 if there is nothing we have to worry about
     */
    private getOverlapNudgePartsCount(): number {
        let result = -1;

        let nudgeCount = 0;
        //check if there is nudge symbol with nudge animation with up direction
        if (
            this.data.transform != null &&
            CustomUtils.NudgeSymbols.indexOf(this.data.symbols[this.symbolCount - 1]) != -1
        ) {
            //count nudge symbols
            for (let i = 0; i < this.data.symbols.length; i++) {
                if (CustomUtils.NudgeSymbols.indexOf(this.data.symbols[i]) > -1) {
                    nudgeCount++;
                }
            }
        }

        if (nudgeCount == 2) {
            //if nudge count is 2, the last part of nudge symbol is bottom symbol
            return 0;
        } else if (nudgeCount == 1) {
            //if nudge count is 1, we have one bottom symbol and one overlapped symbol
            return 1;
        } else if (nudgeCount == 0) {
            //if nudge count in symbols array is 0, we need to check bottom symbol, if there is bottom symbol, we overlap 2 symbols
            const bottomSymbolId =
                this.strip[(this.data.index + this.symbolCount + this.strip.length) % this.strip.length];
            if (CustomUtils.NudgeSymbols.indexOf(bottomSymbolId) > -1) {
                return 2;
            }
        }

        return result;
    }

    /**
     * Set correct case for nudge symbol. The upper one is visible, other 2 parts are empty.
     * @param symbolId - current symbol id added into reel
     * @param symbol - instance of symbol added into reel
     */
    protected updateNudgeSymbolCase(symbolId: number, symbol: Symbol): void {
        if (CustomUtils.NudgeSymbols.indexOf(symbolId) > -1) {
            this.nudgeCurrentPartIndex++;

            if (this.nudgeCurrentPartIndex == CustomUtils.NudgeSymbolPartCount) {
                this.nudgeCurrentPartIndex = 0;
            } else {
                if (symbol != null) {
                    symbol.goToCase(Symbol.CaseEmpty);
                }
            }
        }
    }

    //******************************************************************************************************************
    //  REEL RULES
    //******************************************************************************************************************

    protected getOuterSymbol(index: number): number {
        let ruleData = {} as ReelRuleData;
        ruleData.index = index;
        ruleData.strip = this.strip;
        ruleData.stripIndex =
            (this.data.index + this.symbolBottomCount + this.symbolCount - index - 1 + this.strip.length) %
            this.strip.length;
        ruleData.symbolId = ruleData.strip[ruleData.stripIndex];

        const reelRules: Array<Function> = [];
        for (let i = 0; i < reelRules.length; i++) {
            ruleData = reelRules[i](ruleData);
        }

        return ruleData.symbolId;
    }

    //******************************************************************************************************************
    //  SPIN COMPLETE
    //******************************************************************************************************************

    protected spinComplete(): void {
        Tweens.removeTweens(this, "position");

        if (MachineExtended.NudgeReelIndexes.indexOf(this.index) == -1) {
            while (this.currentSymbols.length > 0) {
                this.returnSymbol(this.currentSymbols.pop());
            }
        }

        this.emit(Reel.ReelStopped, this.index);
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    /**
     * Sorts children of container by symbols position and zIndex attribute.
     */
    protected sortChildrenByZindex(holder: any): void {
        if (!Utils.isNullOrUndefined(holder)) {
            holder.children
                .sort((a: any, b: any) => {
                    a.x = a.x || 0;
                    b.x = b.x || 0;

                    return a.x - b.x; // ascending: a - b; descending b - a
                })
                .sort((a: any, b: any) => {
                    a.y = a.y || 0;
                    b.y = b.y || 0;

                    return a.y - b.y; // ascending: a - b; descending b - a
                })
                .sort((a: any, b: any) => {
                    if (a.hasOwnProperty("zIndex") && b.hasOwnProperty("zIndex")) {
                        a.zIndex = a.zIndex || 0;
                        b.zIndex = b.zIndex || 0;

                        return a.zIndex - b.zIndex;
                    }

                    return 0;
                });
        }
    }

    //******************************************************************************************************************
    //  SET DATA
    //******************************************************************************************************************

    public spinStart(): void {
        super.spinStart();

        this.data = null;
    }

    public setData(data: Communication.Game.IReel) {
        if (this.data == null) {
            this.data = JSON.parse(JSON.stringify(data));
        }
    }

    //******************************************************************************************************************
    //  NUDGE SPIN
    //******************************************************************************************************************

    public nudgeSpin(): void {
        const nudgeCount = this.getNudgeSymbolsCount();
        if (nudgeCount > 0) {
            this.createNudgeSpin(nudgeCount);
        } else {
            this.nudgeSpinFinished();
        }
    }

    private createNudgeSpin(nudgeCount: number): void {
        const direction = this.getNudgeDirection();
        const nudgeMove = this.symbolHeight * nudgeCount * direction;

        this.updateNudgeSpinSymbols(direction);

        Tweens.removeTweens(this, "position");
        Tweens.positionTween(this, {
            tweenX: [this.positionX, this.positionX],
            tweenY: [this.positionY, this.positionY + nudgeMove],
            tweenDuration: ReelExtended.NudgeSpinDuration,
            tweenDelay:
                ReelExtended.NudgeSpinDelay +
                MachineExtended.NudgeReelIndexes.indexOf(this.index) * ReelExtended.NudgeSpinReelDelay,
            tweenEase: "customNudge",
            callbackFunction: this.nudgeSpinFinished,
            callbackScope: this,
        });

        PlayList.play("SND_BaseNudge");
        PlayList.play("SND_BaseNudgeHit");
    }

    private nudgeSpinFinished(): void {
        Tweens.removeTweens(this, "position");

        while (this.currentSymbols.length > 0) {
            this.returnSymbol(this.currentSymbols.pop());
        }

        this.emit(ReelExtended.NudgeReelStopped, this.index);
    }

    //******************************************************************************************************************
    //  NUDGE HELPER
    //******************************************************************************************************************

    private updateNudgeSpinSymbols(direction: number): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            if (CustomUtils.NudgeSymbols.indexOf(this.currentSymbols[i].id + 1) > -1) {
                if (
                    this.currentSymbols[i].getCurrentCase() == Symbol.CaseBlur ||
                    this.currentSymbols[i].getCurrentCase() == Symbol.CaseRotation ||
                    this.currentSymbols[i].getCurrentCase() == Symbol.CaseNowin
                ) {
                    if (direction == 1) {
                        this.currentSymbols[i].goToCase(Symbol.CaseNudgeDown);
                    } else {
                        this.currentSymbols[i].goToCase(Symbol.CaseNudgeUp);
                    }
                }
            } else if (this.currentSymbols[i].id + 1 == CustomUtils.SymbolScatter) {
                this.currentSymbols[i].goToCase(Symbol.CaseNormal);
            } else if (this.currentSymbols[i].getCurrentCase() == Symbol.CaseBlur) {
                this.currentSymbols[i].goToCase(Symbol.CaseRotation);
            }
        }
    }

    private getNudgeDirection(): number {
        return CustomUtils.NudgeSymbols.indexOf(this.data.symbols[0]) == -1 ? -1 : 1;
    }

    private getNudgeSymbolsCount(): number {
        let count = 0;

        for (let i = 0; i < this.data.symbols.length; i++) {
            const symbolId = this.data.symbols[i];

            if (CustomUtils.NudgeSymbols.indexOf(symbolId) == -1) {
                count++;
            }
        }

        return count;
    }
}

export type ReelRuleData = {
    symbolId: number;
    index: number;
    stripIndex: number;
    strip: Array<number>;
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ReelExtended.ts
 **/